// AI Handover Notes popover — shared across Contacts and AI Conversations.
//
// Originally lived inline in pages/Contacts.js. Extracted so the same UX
// (hover-peek, click-to-pin, ⌘/Ctrl+Enter to save, draft autosave per contact)
// can be reused on the Needs Review cards and the Conversation History
// view in pages/AIConversations.js.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaRegStickyNote, FaRegTrashAlt, FaTimes, FaRegQuestionCircle,
} from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";
import { getDemoHandoverNotes } from "../demo/demoContactDetails";

// Mirrors backend _normalise_contact_key in agent_platform.py — lower(trim(email))
// when present, else trimmed clean_mobile / mobile. Used for selection state
// (so toggling persists across page changes) and for the popover identity.
export function getContactKey(c) {
  const email = (c?.email || "").trim().toLowerCase();
  if (email) return `e:${email}`;
  const mobile = (c?.clean_mobile || c?.mobile || "").trim();
  if (mobile) return `m:${mobile}`;
  return null;
}

export function getContactIdentity(c) {
  return {
    contact_email: (c?.email || "").trim().toLowerCase() || null,
    contact_mobile: ((c?.clean_mobile || c?.mobile) || "").trim() || null,
  };
}

// Hook encapsulating the shared open/pin/hover state plus the timers that
// keep hover-mode from flickering when the cursor crosses between the
// trigger button and the popover. Returned helpers:
//   - popoverProps: spread onto <AIHandoverNotesPopover />
//   - openHover(el, contact): open in hover mode (no focus steal)
//   - openPinned(el, contact): open + pin (focus textarea, dismiss on outside click)
//   - closeHover(): schedule a hover-mode close
//   - closePopover(): hard close (used by the popover's X button)
//   - clearTimers(): cancel pending open/close — call from elements that
//                    should keep the popover sticky on hover
export function useHandoverNotesPopover({ isMobile, onNotesChanged } = {}) {
  const [popAnchor, setPopAnchor] = useState(null);
  const [popContact, setPopContact] = useState(null);
  const [popPinned, setPopPinned] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const HOVER_OPEN_MS = 150;
  const HOVER_CLOSE_MS = 180;

  const clearTimers = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);

  const closeHover = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => {
      setPopAnchor(null);
      setPopContact(null);
      setPopPinned(false);
    }, HOVER_CLOSE_MS);
  }, [clearTimers]);

  const openHover = useCallback((el, contact) => {
    clearTimers();
    openTimer.current = setTimeout(() => {
      setPopAnchor(el);
      setPopContact(contact);
    }, HOVER_OPEN_MS);
  }, [clearTimers]);

  const openPinned = useCallback((el, contact) => {
    clearTimers();
    setPopAnchor(el);
    setPopContact(contact);
    setPopPinned(true);
  }, [clearTimers]);

  const closePopover = useCallback(() => {
    clearTimers();
    setPopAnchor(null);
    setPopContact(null);
    setPopPinned(false);
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const popoverProps = {
    anchorEl: popAnchor,
    contact: popContact,
    pinned: popPinned,
    onClose: closePopover,
    onMouseEnter: clearTimers,
    onMouseLeave: () => { if (!popPinned) closeHover(); },
    onNotesChanged,
    isMobile,
  };

  return { popoverProps, openHover, openPinned, closeHover, closePopover, clearTimers, pinned: popPinned };
}

// Convenience trigger button. Used on the Needs Review cards and the
// Conversation History header. Contacts.js renders its own trigger inline
// (the styling there depends on selection / notes-count colour) so it
// doesn't use this helper — it wires the hook helpers directly to its
// existing button.
export function HandoverNotesButton({
  contact,
  notesCount = 0,
  openHover,
  openPinned,
  closeHover,
  size = "sm",
  label,            // override the default label
  style,            // extra style overrides
  iconOnly = false, // render a 32x32 icon-only button (with optional count badge)
}) {
  const ref = useRef(null);
  const key = getContactKey(contact);
  const disabled = !key;
  const fontSize = size === "sm" ? 11 : 12;
  const iconSize = size === "sm" ? 11 : 12;
  const padding = size === "sm" ? "4px 8px" : "6px 10px";
  const text = label
    ?? (notesCount > 0 ? `${notesCount} AI note${notesCount === 1 ? "" : "s"}` : "AI Notes");

  if (iconOnly) {
    const active = notesCount > 0;
    return (
      <button
        ref={ref}
        data-notes-key={key || ""}
        onMouseEnter={() => { if (!disabled) openHover?.(ref.current, contact); }}
        onMouseLeave={() => { if (!disabled) closeHover?.(); }}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          openPinned?.(ref.current, contact);
        }}
        disabled={disabled}
        title="AI Notes"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          position: "relative",
          width: 32, height: 32, borderRadius: 8, padding: 0,
          background: active ? "#fffbeb" : "#fff",
          border: `1px solid ${active ? "#fde68a" : "#e5e7eb"}`,
          color: active ? "#92400e" : "#6b7280",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background 0.12s, border-color 0.12s, color 0.12s",
          ...style,
        }}
      >
        <FaRegStickyNote style={{ fontSize: 14 }} />
        {notesCount > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            minWidth: 14, height: 14, padding: "0 3px", borderRadius: 999,
            background: "#f59e0b", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 700,
            border: "1.5px solid #fff",
          }}>
            {notesCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      ref={ref}
      data-notes-key={key || ""}
      onMouseEnter={() => { if (!disabled) openHover?.(ref.current, contact); }}
      onMouseLeave={() => { if (!disabled) closeHover?.(); }}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        openPinned?.(ref.current, contact);
      }}
      disabled={disabled}
      title="AI Notes"
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding, borderRadius: 8,
        background: notesCount > 0 ? "#fffbeb" : "#f9fafb",
        border: `1px solid ${notesCount > 0 ? "#fde68a" : "#e5e7eb"}`,
        color: notesCount > 0 ? "#92400e" : "#6b7280",
        fontSize, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <FaRegStickyNote style={{ fontSize: iconSize }} />
      {text}
    </button>
  );
}

// AI Handover Notes popover. Renders into a portal and positions itself with
// fixed coordinates relative to the anchor element so it isn't clipped by
// any overflow:auto ancestor. Hover to peek, click to pin,
// ⌘/Ctrl+Enter to save.
export function AIHandoverNotesPopover({
  anchorEl,
  contact,
  pinned,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onNotesChanged,
  isMobile,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [pos, setPos] = useState(null); // null until first measurement
  const [showHelp, setShowHelp] = useState(false);
  const [helpUserToggled, setHelpUserToggled] = useState(false);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  const open = Boolean(anchorEl && contact);
  const identity = contact ? getContactIdentity(contact) : null;
  const contactKey = contact ? getContactKey(contact) : null;
  const draftStorageKey = contactKey ? `vl-contact-handover-note-draft:${contactKey}` : null;

  // Reset position whenever the anchor changes so we don't briefly show the
  // popover at the previous anchor's coordinates.
  useLayoutEffect(() => {
    setPos(null);
  }, [anchorEl]);

  // Position the popover anchored to the button: drops down below by default,
  // right-aligned to the button's right edge. Flips above if there isn't room
  // below. Clamps to the viewport so it's always visible.
  // Skipped on mobile (full-screen modal layout doesn't need positioning).
  useLayoutEffect(() => {
    if (!open || !anchorEl) return;
    if (isMobile) { setPos({ top: 0, left: 0, placement: "fullscreen" }); return; }
    const updatePos = () => {
      // The list re-renders whenever ai_owns_relationship / notes_count change,
      // which can detach the original anchor element. A detached element's
      // getBoundingClientRect() returns all zeros (top-left of viewport), so
      // if the stored anchor is no longer in the DOM we fall back to looking
      // the live button up by data-notes-key. We only use the lookup as a
      // fallback because the same contact can have multiple buttons in the
      // DOM at once (e.g. the /contacts row button plus an in-modal button
      // when AIConversationModal is open), and querySelector would otherwise
      // anchor to the wrong one — typically the row button underneath the
      // modal overlay, making the popover appear far from the clicked button.
      let target = anchorEl;
      if ((!target || !target.isConnected) && contactKey) {
        const live = document.querySelector(
          `[data-notes-key="${contactKey.replace(/"/g, '\\"')}"]`,
        );
        if (live) target = live;
      }
      if (!target) return;
      const r = target.getBoundingClientRect();
      const W = 340;
      const H = 420;
      const margin = 8;
      const gap = 6;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Right-align to button: popover's right edge = button's right edge
      let left = r.right - W;
      if (left < margin) left = margin;
      if (left + W > vw - margin) left = Math.max(margin, vw - W - margin);
      // Drop below by default
      let top = r.bottom + gap;
      if (top + H > vh - margin) {
        // Not enough room below — try above
        const aboveTop = r.top - gap - H;
        if (aboveTop >= margin) {
          top = aboveTop;
        } else {
          // Neither fits — clamp to viewport
          top = Math.max(margin, vh - H - margin);
        }
      }
      setPos({ top, left, placement: "bottom" });
    };
    updatePos();
    const raf = requestAnimationFrame(updatePos);
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, anchorEl, contactKey, isMobile]);

  // Close on outside click (only when pinned — hover-mode closes via mouseleave)
  useEffect(() => {
    if (!open || !pinned) return;
    const onDocClick = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      if (contactKey) {
        const live = document.querySelector(
          `[data-notes-key="${contactKey.replace(/"/g, '\\"')}"]`,
        );
        if (live && live.contains(e.target)) return;
      }
      onClose?.();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, pinned, anchorEl, contactKey, onClose]);

  useEffect(() => {
    if (!open) return;
    setError("");
    // Restore any half-written draft for this contact so accidentally closing
    // the popover doesn't lose work.
    let restored = "";
    if (draftStorageKey) {
      try { restored = window.localStorage.getItem(draftStorageKey) || ""; } catch (_) {}
    }
    setDraft(restored);
    if (!contactKey) {
      setNotes([]);
      setError("This contact has no email or mobile — notes cannot be attached.");
      return;
    }
    // Demo mode: serve sample notes for this contact instead of hitting the
    // backend (which the demo guard blocks). Mirrors the seeded counts on the
    // /contacts sample rows so opening a row's notes shows matching content.
    if (typeof window !== "undefined" && window.__demoMode) {
      const sample = getDemoHandoverNotes(identity);
      setNotes([...sample].sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      ));
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    handleTokenAndPostRequest(
      `${window.api_host}/agent-list-contact-handover-notes`,
      identity,
    )
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.notes || [];
        const sorted = [...list].sort(
          (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
        );
        setNotes(sorted);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message || "Failed to load notes");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, contactKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset help-toggle state when switching contacts so each contact gets a
  // fresh decision (auto-expand when empty, collapse when notes exist).
  useEffect(() => {
    setHelpUserToggled(false);
    setShowHelp(false);
  }, [contactKey]);

  // Auto-expand the explanation only when the contact has no notes yet —
  // once a note exists, the explanation gets out of the way unless the user
  // explicitly opens it.
  useEffect(() => {
    if (loading || helpUserToggled) return;
    setShowHelp(notes.length === 0);
  }, [loading, notes.length, helpUserToggled]);

  // Persist the in-progress draft on every change so the user can recover it
  // if they accidentally close (or click outside) the popover.
  useEffect(() => {
    if (!open || !draftStorageKey) return;
    try {
      if (draft) window.localStorage.setItem(draftStorageKey, draft);
      else window.localStorage.removeItem(draftStorageKey);
    } catch (_) {}
  }, [draft, open, draftStorageKey]);

  // Autofocus only when pinned (hover-open shouldn't steal focus from search)
  useEffect(() => {
    if (open && pinned) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, pinned]);

  const onAdd = async () => {
    const text = draft.trim();
    if (!text || !contactKey) return;
    // Demo mode: append the note locally so the popover stays interactive
    // without persisting anything to the backend.
    if (typeof window !== "undefined" && window.__demoMode) {
      const created = {
        id: `demo-note-${Date.now()}`,
        note: text,
        created_by: "You",
        created_at: new Date().toISOString(),
      };
      setNotes((prev) => [...prev, created]);
      onNotesChanged?.(contact, 1);
      setDraft("");
      if (draftStorageKey) {
        try { window.localStorage.removeItem(draftStorageKey); } catch (_) {}
      }
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/agent-add-contact-handover-note`,
        { ...identity, note: text },
      );
      const created = res.data?.note;
      if (created) {
        setNotes((prev) => [...prev, created]);
        onNotesChanged?.(contact, 1);
      }
      setDraft("");
      if (draftStorageKey) {
        try { window.localStorage.removeItem(draftStorageKey); } catch (_) {}
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (noteId) => {
    setDeletingId(noteId);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/agent-delete-contact-handover-note`,
        { note_id: noteId },
      );
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      onNotesChanged?.(contact, -1);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to delete note");
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  const headerName = contact
    ? ([contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unknown")
    : "";
  const headerSub = contact
    ? [contact.email, contact.clean_mobile || contact.mobile].filter(Boolean).join(" · ")
    : "";

  const node = (
    <div
      ref={popoverRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={isMobile ? {
        position: "fixed",
        inset: 0,
        background: "#fff",
        zIndex: 11000,
        display: "flex",
        flexDirection: "column",
        visibility: pos ? "visible" : "hidden",
        pointerEvents: "auto",
        fontFamily: "inherit",
      } : {
        position: "fixed",
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        width: 340,
        maxHeight: 420,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
        zIndex: 11000,
        display: "flex",
        flexDirection: "column",
        visibility: pos ? "visible" : "hidden",
        pointerEvents: "auto",
        fontFamily: "inherit",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "12px 14px 10px", borderBottom: "1px solid #f3f4f6",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 13, fontWeight: 600, color: "#111827",
          }}>
            <FaRegStickyNote style={{ fontSize: 12, color: "#0d9488" }} />
            AI Handover Notes
            <button
              type="button"
              onClick={() => {
                setHelpUserToggled(true);
                setShowHelp((v) => !v);
              }}
              title={showHelp ? "Hide explanation" : "What are handover notes?"}
              aria-expanded={showHelp}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: showHelp ? "#0d9488" : "#9ca3af", padding: 2,
                fontSize: 12, lineHeight: 1, display: "inline-flex", alignItems: "center",
              }}
            >
              <FaRegQuestionCircle />
            </button>
          </div>
          <div style={{
            marginTop: 2, fontSize: 12, color: "#6b7280",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {headerName}{headerSub ? ` · ${headerSub}` : ""}
          </div>
          {showHelp && (
            <div style={{
              marginTop: 8, padding: "8px 10px", borderRadius: 8,
              background: "#f0fdfa", border: "1px solid #ccfbf1",
              fontSize: 11.5, color: "#0f766e", lineHeight: 1.45,
            }}>
              Instructions and added context for the AI about this contact.
              The AI reads these notes before sending messages or making decisions.
              <div style={{ marginTop: 6, color: "#115e59" }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Examples:</div>
                <div>• Called John — looking for 3-bedders in Manly under $3M.</div>
                <div>• Prefers SMS, not email. Don't contact on weekends.</div>
                <div>• Already sold via another agent — stop all outreach.</div>
                <div>• Investor, interested in off-market opportunities only.</div>
              </div>
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          title="Close"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#9ca3af", padding: 4, fontSize: 12, lineHeight: 1,
          }}
        >
          <FaTimes />
        </button>
      </div>

      {/* Body */}
      <div style={{ overflowY: "auto", padding: "8px 14px", flex: 1, minHeight: 60 }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%",
              border: "2px solid #e5e7eb", borderTopColor: "#0d9488",
              animation: "vlspin 0.8s linear infinite",
            }} />
            <style>{`@keyframes vlspin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : notes.length === 0 ? (
          <div style={{ padding: "16px 0", textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
            No handover notes yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {notes.map((n) => (
              <div key={n.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    <strong style={{ color: "#6b7280", fontWeight: 600 }}>{n.created_by || "agent"}</strong>
                    {n.created_at ? ` · ${new Date(n.created_at).toLocaleString()}` : ""}
                  </span>
                  <button
                    onClick={() => onDelete(n.id)}
                    title="Delete note"
                    disabled={deletingId === n.id}
                    style={{
                      background: "none", border: "none",
                      cursor: deletingId === n.id ? "wait" : "pointer",
                      color: "#9ca3af", padding: 2, fontSize: 11, lineHeight: 1,
                      width: 16, height: 16,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {deletingId === n.id ? (
                      <span style={{
                        display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                        border: "1.5px solid #e5e7eb", borderTopColor: "#9ca3af",
                        animation: "vlspin 0.8s linear infinite",
                      }} />
                    ) : (
                      <FaRegTrashAlt />
                    )}
                  </button>
                </div>
                <div style={{
                  marginTop: 2, fontSize: 13, color: "#1f2937",
                  whiteSpace: "pre-wrap", lineHeight: 1.45,
                  overflowWrap: "anywhere", wordBreak: "break-word",
                }}>
                  {n.note}
                </div>
              </div>
            ))}
          </div>
        )}
        {error && (
          <div style={{
            marginTop: 8, padding: "8px 10px", borderRadius: 8,
            background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: 12,
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <div style={{
        padding: "10px 12px", borderTop: "1px solid #f3f4f6",
        background: "#f9fafb", borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
      }}>
        <textarea
          ref={inputRef}
          placeholder="Note for the AI… (⌘/Ctrl+Enter to save)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              onAdd();
            }
          }}
          disabled={!contactKey}
          rows={3}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "8px 10px", borderRadius: 8,
            border: "1px solid #e5e7eb", background: "#fff",
            fontSize: isMobile ? 16 : 13, fontFamily: "inherit", color: "#111827",
            resize: "vertical", minHeight: 56, maxHeight: 140,
            outline: "none",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "#0d9488"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <button
            onClick={onClose}
            style={{
              padding: "6px 12px", borderRadius: 8,
              background: "#fff", color: "#6b7280",
              border: "1px solid #e5e7eb",
              fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            onClick={onAdd}
            disabled={saving || !draft.trim() || !contactKey}
            style={{
              minWidth: 72,
              padding: "6px 14px", borderRadius: 8,
              background: (saving || !draft.trim() || !contactKey) ? "#a7f3d0" : "#0d9488",
              color: "#fff", border: "none",
              fontSize: 12, fontWeight: 600,
              cursor: saving ? "wait" : ((!draft.trim() || !contactKey) ? "not-allowed" : "pointer"),
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {saving ? (
              <>
                <span style={{
                  display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                  border: "1.5px solid rgba(255,255,255,0.5)", borderTopColor: "#fff",
                  animation: "vlspin 0.8s linear infinite",
                }} />
                Saving
              </>
            ) : "Add"}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

export default AIHandoverNotesPopover;
